import type { SupplyAgentLogisticsCalculateResponse } from '@/services/supplyAgentService';
import { snapshotOf } from './loadEstimateOccupancy';

export interface LoadEstimateSupplierResult {
  supplierId: number;
  supplierName: string;
  result: SupplyAgentLogisticsCalculateResponse;
  /** El cálculo logístico del proveedor falló (sin regla, sin líneas…): sus productos siguen en el pedido pero no cuentan en la carga. */
  unavailableReason?: string;
}

/** Une las estimaciones de varios proveedores en una sola; el contenedor de referencia lo pone `containerSupplierId`. */
export function combineLoadEstimate(
  sources: LoadEstimateSupplierResult[],
  containerSupplierId: number,
): SupplyAgentLogisticsCalculateResponse | null {
  if (sources.length === 0) return null;
  const containerSource = sources.find((source) => source.supplierId === containerSupplierId) || sources[0];
  const container = containerSource.result.container || {};
  const results = sources.map((source) => source.result);
  const lines = sources.flatMap(({ supplierId, supplierName, result }) =>
    (result.before?.lines_detail || []).map((line, lineIndex) => ({
      ...line,
      load_line_id: `${supplierId}:${String(line.product_id ?? lineIndex)}`,
      supplier_id: supplierId,
      supplier_name: supplierName || line.supplier_name || String(supplierId),
    })),
  );
  const sum = (key: 'units' | 'pallet_equivalent' | 'volume_m3' | 'weight_kg') => {
    const rawValues = results.map((result) => result.before?.[key]);
    if (rawValues.some((value) => value == null || !Number.isFinite(Number(value)))) return undefined;
    return rawValues.reduce<number>((total, value) => total + Number(value), 0);
  };
  const volume = sum('volume_m3');
  const weight = sum('weight_kg');
  const usableVolume = Number(container.usable_volume_m3);
  const usableWeight = Number(container.usable_payload_kg);
  const missingData = sources.flatMap(({ supplierId, supplierName, result }) => (result.missing_data || []).map((item) => ({
    ...item,
    supplier_id: supplierId,
    supplier_name: supplierName,
  })));
  const hasWarnings = missingData.length > 0 || results.some((result) =>
    result.status === 'SUCCESS_WITH_WARNINGS' || (result.warnings?.length || 0) > 0,
  );

  return {
    ...containerSource.result,
    mode: 'SIMULATE',
    status: hasWarnings ? 'SUCCESS_WITH_WARNINGS' : 'SUCCESS',
    supplier_id: null,
    before: {
      units: sum('units'),
      pallet_equivalent: sum('pallet_equivalent'),
      volume_m3: volume,
      weight_kg: weight,
      lines_detail: lines,
    },
    after: {
      units: sum('units'),
      pallet_equivalent: sum('pallet_equivalent'),
      volume_m3: volume,
      weight_kg: weight,
      lines_detail: lines,
    },
    lines_detail: lines,
    missing_data: missingData,
    warnings: results.flatMap((result) => result.warnings || []),
    eligible_lines_count: results.reduce((total, result) => total + Number(result.eligible_lines_count || 0), 0),
    skipped_lines_count: results.reduce((total, result) => total + Number(result.skipped_lines_count || 0), 0),
    container,
    utilization: {
      volume_m3_before: volume,
      volume_m3_after: volume,
      weight_kg_before: weight,
      weight_kg_after: weight,
      volume_fill_pct_after: volume != null && usableVolume > 0 ? (volume / usableVolume) * 100 : undefined,
      weight_fill_pct_after: weight != null && usableWeight > 0 ? (weight / usableWeight) * 100 : undefined,
    },
  };
}

/** Nuevas cantidades por línea (`proveedor:producto`) aplicadas a las estimaciones, con los totales recalculados. */
export function withLineQuantities(
  sources: LoadEstimateSupplierResult[],
  quantities: Record<string, number>,
): LoadEstimateSupplierResult[] {
  return sources.map((source) => {
    const lines = (source.result.before?.lines_detail ?? []).map((line) => {
      const quantity = quantities[`${source.supplierId}:${String(line.product_id)}`];
      return quantity == null ? line : { ...line, suggested_units: quantity, suggested_quantity: quantity };
    });
    const snapshot = snapshotOf(lines);
    return { ...source, result: { ...source.result, before: snapshot, after: snapshot, lines_detail: lines } };
  });
}
